import React, { FC } from 'react';

import { Grid, IconButton, Tooltip } from '@mui/material';
import { UploadFileOutlined } from '@mui/icons-material';
import { Box } from '@mui/system';
import { FormikProps } from 'formik';

import {
  adminBroadcastGroupsForDropdown,
  adminLeadFilter,
  HelpTypes,
  LeadStatusType,
} from '@pharmaplan/common';
import { dropdownMapper } from '@pharmaplan/common/helpers/utils';
import { useNavigate } from 'react-router-dom';
import strings from '../../../localization';
import CreateNew from '../../common/CreateNew';
import CustomButton from '../../common/CustomButton';
import CustomDropdown from '../../common/CustomDropdown';
import genericClasses from '../../../theme/GenericClasses';
import CustomTextField from '../../common/CustomTextField';
import { Constants } from '../../../helpers/Constants';
import { useAppSelector } from '../../../hooks/useAppSelector';
import FilterSubmitButtons from '../../common/FilterSubmitButtons';
import { ReactComponent as SendIcon } from '../../../assets/svg/sendMailIcon.svg';

import { styles } from './styles';
import PendingTabs from '../PendingTabs';
import { LeadTabs } from './Leads';

const { pharmacistLeads, technicianLeads, pharmacyLeads } = Constants.paths.admin;

export enum selectAllTypes {
  pharmacies,
  leads,
}

const selectAllTypeDropdown = [
  { key: selectAllTypes.leads, label: strings.leads },
  { key: selectAllTypes.pharmacies, label: strings.users },
];

interface ILeadsHeader {
  formik: FormikProps<any>;
  handleCreate: () => void;
  handleReset: () => void;
  selectedTabs: LeadTabs;
  handleTabClick: (tab: LeadTabs) => void;
  disabled: boolean;
  handleInviteSelected: () => void;
  handleUploadLeads: () => void;
  handleSelectAllTypeChange: (types: Array<selectAllTypes>) => void;
}

const LeadsHeader: FC<ILeadsHeader> = ({
  formik,
  disabled,
  handleReset,
  handleCreate,
  selectedTabs,
  handleTabClick,
  handleUploadLeads,
  handleInviteSelected,
  handleSelectAllTypeChange,
}) => {
  const filters = useAppSelector(adminLeadFilter);
  const broadcastGroups = useAppSelector(adminBroadcastGroupsForDropdown);
  const navigate = useNavigate();

  const handleSelectAllMenuClick = (value: string) => {
    const parsed = Number(value) as selectAllTypes;
    const current: Array<selectAllTypes> = (
      formik.values?.selectAllType ?? []
    ).map(Number);

    const alreadySelected = current.includes(parsed);
    const next = alreadySelected
      ? current.filter((item) =>
        item !== parsed)
      : [...current, parsed];

    formik.setFieldValue('selectAllType', next);
    handleSelectAllTypeChange(next);
  };

  const leadsTabMap = {
    [LeadTabs.pharmacy]: pharmacyLeads,
    [LeadTabs.pharmacist]: pharmacistLeads,
    [LeadTabs.technicians]: technicianLeads,
  };

  const handleTabRoute = (tab: LeadTabs) =>
    () => {
      navigate(leadsTabMap?.[tab] ?? pharmacistLeads);

      handleTabClick(tab);
    };

  const tabConfig = [
    {
      key: HelpTypes.pharmacy,
      isDefault: true,
      tabName: `${strings.pharmacies}`,
      onClick: handleTabRoute(LeadTabs.pharmacy),
    },
    {
      key: HelpTypes.pharmacist,
      tabName: strings.pharmacists,
      onClick: handleTabRoute(LeadTabs.pharmacist),
    },
    {
      key: HelpTypes.technicians,
      tabName: strings.technicians,
      onClick: handleTabRoute(LeadTabs.technicians),
    },
  ];

  const leadStatusDropdown = [
    { key: LeadStatusType.Pending, label: strings.pending },
    { key: LeadStatusType.InviteSent, label: strings.inviteSent },
    {
      key: LeadStatusType.Registered,
      label: strings.registeredPendingActivationList,
    },
    { key: LeadStatusType.RegistrationApproved, label: strings.accountCreated },
    { key: LeadStatusType.RegistrationRejected, label: strings.rejected },
    { key: LeadStatusType.Deleted, label: strings.deleted },
  ];

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <PendingTabs
        header={strings.leads}
        tabConfig={tabConfig}
        selectedTab={selectedTabs}
        EndAdornment={(
          <Box sx={styles.titleContainer}>
            <Box sx={styles.actions}>
              <CustomButton
                fullWidth={false}
                customButtonStyle={styles.uploadLead}
                variant="outlined"
                startIcon={<UploadFileOutlined />}
                label={strings.uploadLeads}
                onClick={handleUploadLeads}
                type="button"
              />
              <CreateNew label={strings.createNew} onClick={handleCreate} />
            </Box>
          </Box>
        )}
      />

      <Grid
        container
        xs={12}
        alignItems="center"
        wrap="nowrap"
        sx={styles.filterContainer}
      >
        <Grid xs={2.5}>
          <CustomTextField
            formik={formik}
            placeholder={strings.enterNameEmail}
            name="leadName"
            label={strings.leadName}
          />
        </Grid>
        <Grid xs={2.5} sx={genericClasses.filterMargin}>
          <CustomDropdown
            formik={formik}
            name="campaignId"
            menuItems={dropdownMapper(filters.campaigns, 'campaignId', 'name')}
            label={strings.campaignName}
          />
        </Grid>
        <Grid xs={2.5} sx={genericClasses.filterMargin}>
          <CustomDropdown
            formik={formik}
            name="broadcastGroupId"
            menuItems={dropdownMapper(
              broadcastGroups.data,
              'broadcastGroupId',
              'name',
            )}
            label={strings.broadcastGroup}
          />
        </Grid>
        <Grid xs={2.5} sx={genericClasses.filterMargin}>
          <CustomDropdown
            formik={formik}
            name="status"
            label={strings.status}
            menuItems={leadStatusDropdown}
            showPlaceholder
          />
        </Grid>
        <FilterSubmitButtons handleReset={handleReset} />
      </Grid>
      <Box sx={styles.selectContainer}>
        <Box sx={styles.selectDropdown}>
          <CustomDropdown
            label={strings.select}
            multiple
            formik={formik}
            name="selectAllType"
            menuItems={selectAllTypeDropdown}
            customClass={{ minWidth: 100 }}
            onClickMenuItem={handleSelectAllMenuClick}
          />
        </Box>
        <Box sx={{ position: 'relative', top: 5 }}>
          <Tooltip title={strings.sendEmail}>
            <IconButton disabled={disabled} onClick={handleInviteSelected}>
              <SendIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
export default LeadsHeader;
